<template>
    <div
        class="scroll-area"
        :class="{
            'overflow-top': isOverflowingTop,
            'overflow-bottom': isOverflowingBottom,
            'hide-bar': hideBar
        }"
    >
        <div class="simplebar-wrapper">
            <div class="simplebar-height-auto-observer-wrapper">
                <div class="simplebar-height-auto-observer" />
            </div>
            <div class="simplebar-mask">
                <div class="simplebar-offset">
                    <div
                        ref="scrollElement"
                        class="simplebar-content-wrapper"
                    >
                        <div
                            ref="contentElement"
                            class="simplebar-content"
                            :class="contentClass"
                        >
                            <slot
                                v-bind="{
                                    isOverflowingBottom,
                                    isOverflowingTop,
                                    isScrollable,
                                    recalculate,
                                    wrapper: $refs.contentElement
                                }"
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div class="simplebar-placeholder" />
        </div>
        <div
            class="simplebar-track simplebar-horizontal"
            :style="{ 'bottom': `${barOffsetSide}px` }"
        >
            <div class="simplebar-scrollbar">
                <div
                    class="simplebar-scrollbar-inner"
                    :style="{ 'left': `${barOffsetHorizontal.start}px`, 'right': `${barOffsetHorizontal.end}px` }"
                />
            </div>
        </div>
        <div
            class="simplebar-track simplebar-vertical"
            :style="{ 'right': `${barOffsetSide}px` }"
        >
            <div class="simplebar-scrollbar">
                <div
                    class="simplebar-scrollbar-inner"
                    :style="{ 'top': `${barOffsetVertical.start}px`, 'bottom': `${barOffsetVertical.end}px` }"
                />
            </div>
        </div>
        <div
            v-if="fade"
            class="text-fade"
            :style="{ borderRadius: `${fadeRadius}px` }"
        >
            <div
                class="text-fade-bar top"
                :style="{
                    '--fade-colour-rgb': fadeColour ? hexToRgb(fadeColour).join(',') : '255, 255, 255'
                }"
            />
            <div
                class="text-fade-bar bottom"
                :style="{
                    '--fade-colour-rgb': fadeColour ? hexToRgb(fadeColour).join(',') : '255, 255, 255'
                }"
            />
        </div>
    </div>
</template>

<script>
import { hexToRgb } from '@/helpers/colour';

import SimpleBar from 'simplebar';

export default {
    name: 'ScrollArea',
    props: {
        options: { type: Object, default: () => ({}) },
        disabled: { type: Boolean, default: false },
        overflowOffset: { type: Number, default: 0 },
        barOffsetHorizontal: { type: Object, default: () => ({ start: 2, end: 2 }) },
        barOffsetVertical: { type: Object, default: () => ({ start: 2, end: 2 }) },
        reactivate: { type: Boolean, default: true },
        barOffsetSide: { type: Number, default: 0 },
        hideBar: { type: Boolean, default: false },
        contentClass: { type: String, default: null },

        fade: { type: Boolean, default: false },
        fadeRadius: { type: Number, default: 0 },
        fadeColour: { type: String, default: null },
    },
    emits: ['mounted', 'scroll:up', 'scroll:down', 'scroll:top', 'scroll:bottom'],
    slots: ['default', 'toggle'],
    data() {
        return {
            simpleBar: null,
            timer: null,
            scrollTop: 0,

            isScrollable: false,
            isOverflowingTop: false,
            isOverflowingBottom: false
        };
    },
    watch: {
        disabled(to) {
            if (to) {
                this.unmountSimplebar();
                return;
            }
            this.mountSimplebar();
        }
    },
    mounted() {
        this.mountSimplebar();
    },
    activated() {
        if (!this.reactivate) return;
        this.recalculate(true);
    },
    beforeUnmount() {
        this.unmountSimplebar();
    },
    methods: {
        hexToRgb,
        scrollTo(args) {
            if (!this.simpleBar) return;
            const scrollEl = this.simpleBar.getScrollElement();
            scrollEl.scrollTo(args);
        },
        mountSimplebar() {
            if (!this.$el) return;
            if (this.disabled) return;

            this.simpleBar = new SimpleBar(this.$el, this.options);
            const element = this.simpleBar.getScrollElement();

            element.addEventListener('scroll', this.onScroll);
            this.$timeout(() => this.checkOverflow(element, true), 100);
            this.$emit('mounted');
        },
        unmountSimplebar() {
            if (this.simpleBar && this.simpleBar.unMount) this.simpleBar.unMount();
        },
        recalculate(isFull = false) {
            if (!this.simpleBar) return;

            if (!isFull) {
                this.simpleBar.recalculate();
                return;
            }

            this.unmountSimplebar();
            this.mountSimplebar();
        },
        onScroll({ target }) {
            this.checkOverflow(target);
        },
        checkOverflow(target, setup = false) {
            if (!target) return;
            const { scrollTop, scrollHeight, clientHeight } = target;
            if (clientHeight === 0) return this.$timeout(() => this.checkOverflow(this.$refs.scrollElement, setup), 100);
            const previousScrollTop = this.scrollTop;

            this.scrollTop = scrollTop;
            this.isOverflowingTop = scrollTop > this.overflowOffset;
            this.isOverflowingBottom = scrollHeight - scrollTop - clientHeight > this.overflowOffset;
            this.isScrollable = scrollHeight > clientHeight;

            if (!setup) {
                if (previousScrollTop > scrollTop) {
                    if (this.isOverflowingTop) this.$emit('scroll:up');
                } else if (previousScrollTop < scrollTop) {
                    if (this.isOverflowingBottom) this.$emit('scroll:down');
                }
            }

            if (scrollTop === 0) this.$emit('scroll:top');
            if (scrollHeight - scrollTop === clientHeight) this.$emit('scroll:bottom');
        },
        getScrollElement() {
            if (!this.simpleBar) return null;
            return this.simpleBar.getScrollElement();
        }
    },
};
</script>

<style lang="less">

.text-fade {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: hidden;
    pointer-events: none;
    .text-fade-bar {
        position: absolute;
        height: 40px;
        left: 0;
        right: 0;
        opacity: 0;
        transition: opacity 250ms var(--curve);
        &.top {
            top: 0;
            background-image: linear-gradient(0deg,
                ~'rgba(var(--fade-colour-rgb), 0)' 0%,
                ~'rgba(var(--fade-colour-rgb), 1)' 100%
            );
        }
        &.bottom {
            bottom: 0;
            background-image: linear-gradient(180deg,
                ~'rgba(var(--fade-colour-rgb), 0)' 0%,
                ~'rgba(var(--fade-colour-rgb), 1)' 100%
            );
        }
    }
}

.scroll-area {
    max-height: 100%;
    &.overflow-top {
        > .text-fade .text-fade-bar {
            &.top {
                opacity: 1;
            }
        }
    }
    &.overflow-bottom {
        > .text-fade .text-fade-bar {
            &.bottom {
                opacity: 1;
            }
        }
    }
    &.hide-bar {
        .simplebar-scrollbar .simplebar-scrollbar-inner {
            opacity: 0;
        }
    }
}

[data-simplebar] {
    position: relative;
    flex-direction: column;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: flex-start;
    &.simplebar-dragging {
        .simplebar-content {
            pointer-events: none;
            user-select: none;
            -webkit-user-select: none;
        }
        .simplebar-track {
            pointer-events: all;
        }
    }
}

.simplebar-wrapper {
    overflow: hidden;
    width: inherit;
    height: inherit;
    max-width: inherit;
    max-height: inherit;
}

.simplebar-mask {
    direction: inherit;
    position: absolute;
    overflow: hidden;
    padding: 0;
    margin: 0;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    width: auto !important;
    height: auto !important;
    z-index: 0;
}

.simplebar-offset {
    direction: inherit !important;
    box-sizing: inherit !important;
    resize: none !important;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    padding: 0;
    margin: 0;
    -webkit-overflow-scrolling: touch;
}

.simplebar-content-wrapper {
    direction: inherit;
    scroll-behavior: smooth;
    box-sizing: border-box !important;
    position: relative;
    display: block;
    height: 100%;
    /* Required for horizontal native scrollbar to not appear if parent is taller than natural height */
    width: auto;
    max-width: 100%;
    /* Not required for horizontal scroll to trigger */
    max-height: 100%;
    /* Needed for vertical scroll to trigger */
    scrollbar-width: none;
    -ms-overflow-style: none;
}

.simplebar-content-wrapper::-webkit-scrollbar,
.simplebar-hide-scrollbar::-webkit-scrollbar {
    width: 0;
    height: 0;
}

.simplebar-content:before,
.simplebar-content:after {
    content: ' ';
    display: table;
}

.simplebar-placeholder {
    max-height: 100%;
    max-width: 100%;
    width: 100%;
    pointer-events: none;
}

.simplebar-height-auto-observer-wrapper {
    box-sizing: inherit !important;
    height: 100%;
    width: 100%;
    max-width: 1px;
    position: relative;
    float: left;
    max-height: 1px;
    overflow: hidden;
    z-index: -1;
    padding: 0;
    margin: 0;
    pointer-events: none;
    flex-grow: inherit;
    flex-shrink: 0;
    flex-basis: 0;
}

.simplebar-height-auto-observer {
    box-sizing: inherit;
    display: block;
    opacity: 0;
    position: absolute;
    top: 0;
    left: 0;
    height: 1000%;
    width: 1000%;
    min-height: 1px;
    min-width: 1px;
    overflow: hidden;
    pointer-events: none;
    z-index: -1;
}
.simplebar-track {
    z-index: 1;
    position: absolute;
    right: 0;
    bottom: 0;
    pointer-events: none;
    overflow: hidden;
}
[data-simplebar].simplebar-dragging .simplebar-content {
    pointer-events: none;
    user-select: none;
    -webkit-user-select: none;
}
[data-simplebar].simplebar-dragging .simplebar-track {
    pointer-events: all;
}
.simplebar-scrollbar {
    position: absolute;
    left: 0;
    right: 0;
    min-height: 10px;
}
.simplebar-scrollbar .simplebar-scrollbar-inner {
    position: absolute;
    content: '';
    background: black;
    border-radius: 7px;
    left: 2px;
    right: 2px;
    opacity: 0;
    transition: opacity 0.2s linear;
}
.simplebar-scrollbar.simplebar-visible .simplebar-scrollbar-inner {
    /* When hovered, remove all transitions from drag handle */
    opacity: 0.5;
    transition: opacity 0s linear;
}
.simplebar-track.simplebar-vertical {
    top: 0;
    width: 11px;
}
.simplebar-track.simplebar-vertical .simplebar-scrollbar .simplebar-scrollbar-inner {}
.simplebar-track.simplebar-horizontal {
    left: 0;
    height: 11px;
}
.simplebar-track.simplebar-horizontal .simplebar-scrollbar .simplebar-scrollbar-inner {
    height: 100%;
}
.simplebar-track.simplebar-horizontal .simplebar-scrollbar {
    right: auto;
    left: 0;
    top: 2px;
    height: 7px;
    min-height: 0;
    min-width: 10px;
    width: auto;
}
/* Rtl support */
[data-simplebar-direction='rtl'] .simplebar-track.simplebar-vertical {
    right: auto;
    left: 0;
}
.hs-dummy-scrollbar-size {
    direction: rtl;
    position: fixed;
    opacity: 0;
    visibility: hidden;
    height: 500px;
    width: 500px;
    overflow-y: hidden;
    overflow-x: scroll;
}
.simplebar-hide-scrollbar {
    position: fixed;
    left: 0;
    visibility: hidden;
    overflow-y: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;
}
</style>
